import React, { useEffect, useRef, useState } from "react";

function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

export default function paginationFactory(options = {}) {
  return options || {};
}

/**
 * Drop-in replacement for react-bootstrap-table2-paginator's PaginationProvider.
 *
 * Manages page/sizePerPage state and exposes them via `paginationProps`. Any
 * extra (non-standard) keys passed to `paginationFactory` — e.g. the cursor
 * pagination's `offset`, `ids` and `setIds` — are forwarded through to
 * `paginationProps` so custom pagination links can read them, matching the
 * behaviour of the original library.
 *
 * @param {object} props - Component props.
 * @param {object} props.pagination - Options object returned by `paginationFactory`.
 * @param {(args: {paginationProps: object, paginationTableProps: object}) => React.ReactNode} props.children - Render-prop receiving pagination props.
 * @returns {React.ReactNode} The rendered children.
 */
export function PaginationProvider({ pagination, children }) {
  const opts = pagination || {};
  const [page, setPage] = useState(opts.page ?? 1);
  const [sizePerPage, setSizePerPage] = useState(opts.sizePerPage ?? 10);

  useEffect(() => {
    if (opts.page !== undefined && opts.page !== page) setPage(opts.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.page]);

  useEffect(() => {
    if (opts.sizePerPage !== undefined && opts.sizePerPage !== sizePerPage) {
      setSizePerPage(opts.sizePerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.sizePerPage]);

  const rawList = opts.sizePerPageList || [3, 5, 10];
  const sizePerPageList = rawList.map((it) =>
    typeof it === "object"
      ? { text: String(it.text ?? it.page ?? it.value), page: it.page ?? it.value }
      : { text: String(it), page: it }
  );

  const paginationProps = {
    // Forward any custom options (e.g. cursor pagination's offset/ids/setIds)
    // so custom pagination links can read them; managed keys below take precedence.
    ...opts,
    page,
    sizePerPage,
    sizePerPageList,
    totalSize: opts.totalSize ?? 0,
    dataSize: opts.totalSize ?? 0,
    paginationSize: 5,
    onPageChange: (nextPage) => setPage(nextPage),
    onSizePerPageChange: (nextSize) => {
      setSizePerPage(nextSize);
      setPage(1);
    },
  };

  const paginationTableProps = { page, sizePerPage };

  return children({ paginationProps, paginationTableProps });
}

export function BootstrapTable({
  keyField = "id",
  data,
  columns = [],
  defaultSorted,
  onTableChange,
  selectRow,
  rowEvents,
  hover,
  bordered = true,
  bootstrap4, // eslint-disable-line no-unused-vars
  remote, // eslint-disable-line no-unused-vars
  wrapperClasses = "",
  classes = "",
  bodyClasses = "",
  headerClasses = "",
  noDataIndication,
  page,
  sizePerPage,
  children, // eslint-disable-line no-unused-vars
}) {
  const rows = Array.isArray(data) ? data : [];
  const initialSort = defaultSorted?.[0];
  const [sortField, setSortField] = useState(initialSort?.dataField);
  const [sortOrder, setSortOrder] = useState(initialSort?.order);

  const paginationDidMount = useRef(false);
  useEffect(() => {
    if (!paginationDidMount.current) {
      paginationDidMount.current = true;
      return;
    }
    onTableChange?.("pagination", {
      page,
      sizePerPage,
      sortField,
      sortOrder,
      data: rows,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sizePerPage]);

  const sortDidMount = useRef(false);
  useEffect(() => {
    if (!sortDidMount.current) {
      sortDidMount.current = true;
      return;
    }
    onTableChange?.("sort", {
      page,
      sizePerPage,
      sortField,
      sortOrder,
      data: rows,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder]);

  const handleSort = (col) => {
    if (!col.sort) return;
    if (sortField !== col.dataField) {
      setSortField(col.dataField);
      setSortOrder("asc");
    } else {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    }
  };

  const tableClassName = [
    "table",
    bordered ? "" : "table-borderless",
    hover ? "table-hover" : "",
    classes,
  ]
    .filter(Boolean)
    .join(" ");

  const hasSelect = !!selectRow;
  const totalCols = columns.length + (hasSelect ? 1 : 0);

  return (
    <div className={wrapperClasses}>
      <table className={tableClassName}>
        <thead className={headerClasses}>
          <tr>
            {hasSelect && (
              <th style={{ width: 30 }}>
                {selectRow.hideSelectAll
                  ? null
                  : selectRow.selectionHeaderRenderer
                  ? selectRow.selectionHeaderRenderer()
                  : null}
              </th>
            )}
            {columns.map((col, colIndex) => {
              const isSorted = sortField === col.dataField;
              const order = isSorted ? sortOrder : undefined;
              const extraClass =
                typeof col.headerSortingClasses === "function"
                  ? col.headerSortingClasses(col, order, false, colIndex)
                  : col.headerSortingClasses || "";
              return (
                <th
                  key={col.dataField || colIndex}
                  className={extraClass}
                  style={
                    typeof col.headerStyle === "function"
                      ? col.headerStyle(col, colIndex)
                      : col.headerStyle
                  }
                  onClick={col.sort ? () => handleSort(col) : undefined}
                  role={col.sort ? "button" : undefined}
                >
                  {col.headerFormatter
                    ? col.headerFormatter(col, colIndex)
                    : col.text}
                  {col.sort && col.sortCaret ? col.sortCaret(order, col) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className={bodyClasses}>
          {rows.length === 0 && noDataIndication ? (
            <tr>
              <td colSpan={totalCols}>
                {typeof noDataIndication === "function"
                  ? noDataIndication()
                  : noDataIndication}
              </td>
            </tr>
          ) : null}
          {rows.map((row, rowIndex) => (
            <tr
              key={getByPath(row, keyField) ?? rowIndex}
              onClick={(e) => rowEvents?.onClick?.(e, row, rowIndex)}
              style={rowEvents?.onClick ? { cursor: "pointer" } : undefined}
            >
              {hasSelect && (
                <td
                  style={{ width: 30 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {selectRow.selectionRenderer
                    ? selectRow.selectionRenderer({ rowIndex, row })
                    : null}
                </td>
              )}
              {columns.map((col, colIndex) => {
                const value = getByPath(row, col.dataField);
                const content = col.formatter
                  ? col.formatter(value, row, rowIndex, col.formatExtraData)
                  : value;
                return (
                  <td
                    key={col.dataField || colIndex}
                    className={col.classes}
                    style={
                      typeof col.style === "function"
                        ? col.style(value, row, rowIndex, col.formatExtraData)
                        : col.style
                    }
                  >
                    {content ?? ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
